import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { SHARE, copyText } from "@/lib/api";
import { waLink } from "@/lib/wa";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { IMAGES } from "@/constants/images";
import StockBadge from "@/components/public/StockBadge";
import CTASection from "@/components/public/CTASection";
import Seo from "@/components/Seo";
import { ArrowLeft, FlaskConical, FileText, MessageCircle, Link2 } from "lucide-react";

const SPEC_FIELDS = [
  ["Origin", "origin"], ["Region", "region"], ["Variety", "variety"], ["Process", "process"],
  ["Altitude", "altitude"], ["Grade", "grade"], ["Screen Size", "screen_size"], ["Moisture", "moisture"],
  ["Crop Year", "crop_year"], ["Cupping Notes", "cupping_notes"], ["Packaging", "packaging"], ["MOQ", "moq"],
];

export default function ProductDetail() {
  const { slug } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/products/${slug}`);
        setProduct(res.data);
      } catch (e) { setError(true); } finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return <div className="flex min-h-screen items-center justify-center pt-20 font-mono text-sm text-espresso/50">{t("common.loading")}</div>;
  if (error || !product) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 pt-20">
      <Seo title="Product not found | Warehouse Coffee" noindex />
      <p className="font-body text-espresso/60">Product not found.</p>
      <Link to="/available-stock" className="rounded-full bg-forest px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-offwhite">Back to Stock</Link>
    </div>
  );

  const specs = SPEC_FIELDS.filter(([, k]) => product[k]);

  const seoDesc = [
    `${product.product_name} green coffee from ${product.region ? product.region + ", " : ""}${product.origin || "Indonesia"}.`,
    [product.process, product.grade, product.screen_size && `screen ${product.screen_size}`, product.crop_year && `crop ${product.crop_year}`].filter(Boolean).join(", "),
    product.cupping_notes && `Cupping notes: ${product.cupping_notes}.`,
    "Request samples, specifications and price from Warehouse Coffee.",
  ].filter(Boolean).join(" ");

  const pslug = product.slug || product.id;
  const shareUrl = SHARE.product(pslug);
  const shareImage = product.image ? SHARE.productImage(pslug, (product.updated_at || "").replace(/[^0-9]/g, "").slice(0, 14)) : undefined;
  const onCopy = async () => {
    const ok = await copyText(shareUrl);
    if (ok) toast.success(t("share.copied")); else toast.error("Copy failed");
  };

  return (
    <div>
      <Seo title={`${product.product_name}${product.process ? " - " + product.process : ""} Green Coffee | Warehouse Coffee`} description={seoDesc} image={shareImage} path={`/product/${pslug}`} type="product" />
      <div className="bg-forest-dark pt-24 pb-10">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-offwhite/60 hover:text-amber-soft"><ArrowLeft className="h-4 w-4" /> Back</button>
        </div>
      </div>
      <section className="mx-auto max-w-[1400px] px-4 pb-8 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="lg:-mt-24">
            <div className="relative aspect-square overflow-hidden rounded-2xl border border-forest/10 bg-beige shadow-xl">
              {product.image ? (
                <img src={product.image} alt={product.product_name} className="h-full w-full object-cover" />
              ) : (
                <img src={IMAGES.greenBeans} alt={product.product_name} className="h-full w-full object-cover" />
              )}
              <div className="absolute left-4 top-4"><StockBadge status={product.stock_status} testId="detail-stock-badge" /></div>
            </div>
          </div>
          <div className="pt-4">
            <div className="mb-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-brand">{product.category || "Indonesian Arabica"}</div>
            <h1 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-forest-dark sm:text-4xl lg:text-5xl">{product.product_name}</h1>
            <p className="mt-4 font-body text-base leading-relaxed text-espresso/75">{product.description || "Indonesian Arabica green coffee sourced from selected producing areas in Indonesia."}</p>

            {product.available_stock > 0 && (
              <div className="mt-6 inline-flex items-center gap-2 rounded-lg border border-forest/15 bg-white px-4 py-3">
                <span className="font-mono text-[11px] uppercase tracking-wider text-espresso/50">Available</span>
                <span className="font-display text-xl font-bold text-forest">{product.available_stock.toLocaleString()} {product.stock_unit}</span>
              </div>
            )}

            <div className="mt-8">
              <h2 className="mb-4 font-display text-lg font-bold text-forest-dark">Specifications</h2>
              {specs.length === 0 ? (
                <p className="rounded-lg border border-dashed border-forest/20 bg-beige/50 px-4 py-5 font-body text-sm text-espresso/60">Specifications available upon quotation.</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-forest/10">
                  <table className="w-full text-left">
                    <tbody>
                      {specs.map(([label, k], i) => (
                        <tr key={k} className={i % 2 ? "bg-beige/40" : "bg-white"}>
                          <td className="w-1/3 px-5 py-3 font-mono text-[11px] uppercase tracking-wider text-espresso/55">{label}</td>
                          <td className="px-5 py-3 font-body text-sm font-medium text-forest-dark">{product[k]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-8 rounded-xl bg-forest p-6">
              <h3 className="font-display text-lg font-bold text-offwhite">Interested in this coffee?</h3>
              <p className="mt-1 font-body text-sm text-offwhite/70">Request a sample or a commercial quotation from our team.</p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Link to={`/request-quote?product=${encodeURIComponent(product.product_name)}&sample=1`} data-testid="detail-sample-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-brand px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-espresso-dark transition-all hover:bg-amber-soft"><FlaskConical className="h-4 w-4" /> {t("cta.sample")}</Link>
                <Link to={`/request-quote?product=${encodeURIComponent(product.product_name)}`} data-testid="detail-quote-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-offwhite/30 px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-offwhite transition-all hover:bg-offwhite/10"><FileText className="h-4 w-4" /> {t("cta.quote")}</Link>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="product-share">
              <span className="mr-1 font-mono text-[11px] uppercase tracking-wider text-espresso/55">{t("share.product")}</span>
              <a href={waLink("", `${t("share.productText")} ${product.product_name} - ${shareUrl}`)} target="_blank" rel="noreferrer" data-testid="product-share-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/50 bg-white px-4 py-2 font-display text-[11px] font-bold uppercase tracking-wider text-[#128C7E] transition-colors hover:bg-[#25D366]/10">
                <MessageCircle className="h-4 w-4" /> {t("share.whatsapp")}
              </a>
              <button type="button" onClick={onCopy} data-testid="product-share-copy" className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-white px-4 py-2 font-display text-[11px] font-bold uppercase tracking-wider text-forest transition-colors hover:bg-forest/5">
                <Link2 className="h-4 w-4" /> {t("share.copy")}
              </button>
            </div>
          </div>
        </div>
      </section>
      <div className="mt-10"><CTASection /></div>
    </div>
  );
}
